import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { FUNDS_INVESTMENTS_TITLE } from 'common/constants/funds';
import { FUND_TYPE } from 'common/constants/pageTypes';
import { FUND_SUMMARY_SPREADSHEET_TITLE } from 'common/constants/services/funds';
import { useStore } from 'common/store';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { getFundInvestmentsByMD, getFundSummaryByMD } from 'services/hooks/funds';
import { Logger, useUserUtils } from 'utilities';
import {
  BaseExcelExportDialog,
  FormulatedCheckbox,
  OtherOptions,
  RadioOption,
  useBaseExcelExportForm,
  useBaseExcelExportState,
  useExcelExportLogic,
} from '../BaseExcelExportDialog';
import { BaseDialogProps, ExcelExportContextType } from '../types';
import {
  FundCurrentSelectedValues,
  FundExcelExportDialogErrors,
  OtherOptions as FundOtherOptions,
  HasFundData,
} from './FundExcelExportDialog.types';
import { FUND_EXPORT_OPTIONS, FundExportOption } from '../constants';

export const FundExcelExportDialog = ({ open, onClose }: BaseDialogProps) => {
  const [{ fundInfo }] = useStore() as unknown as UseStoreValues;
  const { currentMeasurementDate, pageType } = useContext(LayoutContext) as unknown as LayoutContextValues;
  const { isLoading: isExportLoading, setCurrentSelectedValues } = useContext(
    ExcelExportContext
  ) as unknown as ExcelExportContextType;

  const [selectedOption, setSelectedOption] = useState<FundExportOption>(FUND_EXPORT_OPTIONS.FUND);
  const [hasFundSummary, setHasFundSummary] = useState(false);
  const [hasFundInvestments, setHasFundInvestments] = useState(false);
  const [errors, setErrors] = useState<FundExcelExportDialogErrors>({} as FundExcelExportDialogErrors);

  const [otherOptions, setOtherOptions] = useState<FundOtherOptions>({
    fundSummary: false,
    scheduleOfInvestments: false,
  });

  const { isStaffUser } = useUserUtils();

  const { formValues, updateFormValues } = useBaseExcelExportForm();
  const { formulated, setFormulated, isLoading, setIsLoading, dataLoadError, setDataLoadError } =
    useBaseExcelExportState();

  const { generateReport, generateFundSummaryReport } = useExcelExportLogic({
    pageType,
    firmInfo: null,
    companyInfo: null,
    fundInfo,
    currentMeasurementDate,
    formValues,
    setErrors,
  });

  useEffect(() => {
    updateFormValues({
      export_fund_summary: Boolean(hasFundSummary),
      export_fund_investments: Boolean(hasFundInvestments),
      export_not_formulated: !formulated,
    });
  }, [hasFundSummary, hasFundInvestments, formulated, updateFormValues]);

  const handleExport = useCallback(() => {
    if (selectedOption === FUND_EXPORT_OPTIONS.FUND) {
      return generateReport({
        export_fund_summary: hasFundSummary,
        export_fund_investments: hasFundInvestments,
        export_not_formulated: !formulated,
        export_formulated_and_print_ready: false,
      });
    }
    if (selectedOption === FUND_EXPORT_OPTIONS.FUND_SUMMARY) {
      return generateFundSummaryReport();
    }
    if (selectedOption === FUND_EXPORT_OPTIONS.OTHER) {
      const { fundSummary: export_fund_summary, scheduleOfInvestments: export_fund_investments } = otherOptions;

      const otherFormValues = {
        export_fund_summary,
        export_fund_investments,
        export_not_formulated: !formulated,
        export_formulated_and_print_ready: false,
      };
      return generateReport(otherFormValues);
    }
    return undefined;
  }, [
    selectedOption,
    hasFundSummary,
    hasFundInvestments,
    formulated,
    generateReport,
    generateFundSummaryReport,
    otherOptions,
  ]);

  const hasAtLeastOneReport = hasFundSummary || hasFundInvestments;
  const hasAtLeastOneOtherOption = Object.values(otherOptions).some(option => option);
  const isValid =
    isEmpty(errors) &&
    hasAtLeastOneReport &&
    (selectedOption === FUND_EXPORT_OPTIONS.FUND ||
      (selectedOption === FUND_EXPORT_OPTIONS.FUND_SUMMARY && hasFundSummary) ||
      (selectedOption === FUND_EXPORT_OPTIONS.OTHER && hasAtLeastOneOtherOption));

  const isExportDisabled = useMemo(
    () => isLoading || isExportLoading || !isValid,
    [isLoading, isExportLoading, isValid]
  );

  useEffect(() => {
    const loadFundData = async () => {
      if (!open || !fundInfo?.id || !currentMeasurementDate?.id || pageType !== FUND_TYPE) {
        return;
      }

      setIsLoading(true);
      setDataLoadError(null);

      const hasFundSummaryData: HasFundData = { has_fund_summary: false, has_fund_investments: false };

      try {
        const fundSummary = await getFundSummaryByMD({
          fundId: fundInfo.id,
          measurementDateId: currentMeasurementDate.id,
        });
        hasFundSummaryData.has_fund_summary = !isEmpty(fundSummary?.companies);

        const fundInvestments = await getFundInvestmentsByMD({
          fundId: fundInfo.id,
          measurementDateId: currentMeasurementDate.id,
          checkForExport: true,
        });
        hasFundSummaryData.has_fund_investments = !isEmpty(fundInvestments?.companies);

        setHasFundSummary(hasFundSummaryData.has_fund_summary);
        setHasFundInvestments(hasFundSummaryData.has_fund_investments);

        const contextValues: Partial<FundCurrentSelectedValues> = {};
        setCurrentSelectedValues(contextValues);
      } catch (error) {
        Logger.error('Error loading fund data', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFundData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fundInfo?.id, currentMeasurementDate?.id, pageType]);

  const title = `${fundInfo?.name} Excel Export`;

  const renderSubOptions = (currentSelectedOption: string) => (
    <>
      <RadioOption value={FUND_EXPORT_OPTIONS.FUND} label="Fund" />
      {isStaffUser() && (
        <FormulatedCheckbox
          formulated={formulated}
          setFormulated={setFormulated}
          selectedOption={currentSelectedOption}
          showCondition={currentSelectedOption === FUND_EXPORT_OPTIONS.FUND}
        />
      )}
      <RadioOption value={FUND_EXPORT_OPTIONS.FUND_SUMMARY} label={FUND_SUMMARY_SPREADSHEET_TITLE} />
      <RadioOption value={FUND_EXPORT_OPTIONS.OTHER} label="Other" />
    </>
  );

  const handleSetOtherOptions = useCallback(
    (options: Record<string, boolean>) => {
      setOtherOptions(options as FundOtherOptions);
    },
    [setOtherOptions]
  );

  const handleSetSelectedOption = useCallback(
    (option: string) => {
      setSelectedOption(option as FundExportOption);
    },
    [setSelectedOption]
  );

  const renderOtherOptions = (currentSelectedOption: string) => (
    <OtherOptions
      otherOptions={otherOptions}
      setOtherOptions={handleSetOtherOptions}
      selectedOption={currentSelectedOption}
      showCondition={currentSelectedOption === FUND_EXPORT_OPTIONS.OTHER}
      options={[
        { key: 'fundSummary', label: FUND_SUMMARY_SPREADSHEET_TITLE, disabled: !hasFundSummary },
        { key: 'scheduleOfInvestments', label: FUNDS_INVESTMENTS_TITLE, disabled: !hasFundInvestments },
      ]}
    />
  );

  return (
    <BaseExcelExportDialog
      open={open}
      onClose={onClose}
      title={title}
      selectedOption={selectedOption}
      setSelectedOption={handleSetSelectedOption}
      formulated={formulated}
      setFormulated={setFormulated}
      otherOptions={otherOptions}
      setOtherOptions={handleSetOtherOptions}
      hasAtLeastOneReport={hasAtLeastOneReport}
      hasAtLeastOneOtherOption={hasAtLeastOneOtherOption}
      isOtherOptionValid={true}
      errors={errors}
      setErrors={setErrors}
      dataLoadError={dataLoadError}
      isLoading={isLoading}
      isExportDisabled={isExportDisabled}
      isExportLoading={isExportLoading}
      onExport={handleExport}
      renderSubOptions={renderSubOptions}
      renderOtherOptions={renderOtherOptions}
    />
  );
};
